import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { LucideDynamicIcon } from "@lucide/angular";
import { backend } from "@/integrations/backend/client";
import { AuthService, Profile, User } from "../core/auth.service";
import { HUB_OPTIONS, normalizeHub, type HubKey } from "../core/hubs";
import { LanguageService } from "../core/language.service";
import { ToastService } from "../core/toast.service";
import { LanguageSelectorComponent } from "../shared/language-selector.component";
import {
  buttonClasses,
  cardClasses,
  inputClasses,
  labelClasses,
} from "../shared/ui";

function profileStatusColumnMissing(error: unknown) {
  const text =
    error && typeof error === "object"
      ? JSON.stringify(error).toLowerCase()
      : String(error ?? "").toLowerCase();

  return (
    text.includes("profile_status_message") ||
    text.includes("42703")
  );
}

function errorMessage(error: unknown) {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object") {
    const record = error as {
      message?: unknown;
      details?: unknown;
      status?: unknown;
    };
    const message =
      typeof record.message === "string" ? record.message : undefined;
    const details =
      typeof record.details === "string" ? record.details : undefined;
    const status =
      typeof record.status === "number" || typeof record.status === "string"
        ? `Status ${record.status}`
        : undefined;

    return [message, details, status].filter(Boolean).join(" ");
  }

  return String(error || "");
}

function isValidEmail(email: string) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
}

function isEmailAlreadyRegisteredError(error: unknown) {
  const text = errorMessage(error).toLowerCase();
  return (
    text.includes("already registered") ||
    text.includes("users_email_key") ||
    text.includes("duplicate")
  );
}

@Component({
  selector: "app-settings-page",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LanguageSelectorComponent,
    LucideDynamicIcon,
  ],
  template: `
    <div class="min-h-screen bg-background bg-ocean-pattern">
      <header
        class="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div class="container max-w-2xl mx-auto px-4 py-4">
          <div class="flex items-center gap-4">
            <button
              [class]="buttonClasses('ghost', 'icon')"
              (click)="router.navigateByUrl('/')"
            >
              <svg lucideIcon="arrow-left" class="h-5 w-5"></svg>
            </button>
            <div class="flex items-center gap-3">
              <div class="p-2 rounded-full bg-muted">
                <svg
                  lucideIcon="settings"
                  class="h-6 w-6 text-muted-foreground"
                ></svg>
              </div>
              <h1 class="text-xl font-bold text-foreground">
                {{ language.t("settings.title") }}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <div class="container max-w-2xl mx-auto px-3 py-6 sm:px-4 sm:py-8">
        <section [class]="cardClasses()">
          <header class="flex flex-col space-y-1.5 p-6">
            <h2
              class="text-2xl font-semibold leading-none tracking-normal flex items-center gap-2"
            >
              <svg lucideIcon="user" class="h-5 w-5"></svg>
              {{ language.t("settings.profileTitle") }}
            </h2>
            <p class="text-sm text-muted-foreground">
              {{ language.t("settings.profileDescription") }}
            </p>
          </header>
          <div class="p-4 pt-0 space-y-6 sm:p-6 sm:pt-0">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div
                class="mx-auto h-24 w-24 shrink-0 rounded-full bg-primary/20 flex items-center justify-center text-3xl overflow-hidden sm:mx-0 sm:h-20 sm:w-20"
              >
                <img
                  *ngIf="avatarUrl"
                  [src]="avatarUrl"
                  alt="Avatar"
                  class="w-full h-full object-cover"
                  (error)="avatarUrl = ''"
                />
                <span *ngIf="!avatarUrl">🧑‍✈️</span>
              </div>
              <div class="w-full min-w-0 flex-1 space-y-3">
                <label [class]="labelClasses()" for="avatar-url">{{
                  language.t("settings.avatarUrl")
                }}</label>
                <input
                  [class]="inputClasses()"
                  id="avatar-url"
                  name="avatarUrl"
                  type="url"
                  placeholder="https://example.com/avatar.jpg"
                  [(ngModel)]="avatarUrl"
                />
                <div
                  class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <p class="text-xs leading-5 text-muted-foreground">
                    {{ language.t("settings.avatarUploadHint") }}
                  </p>
                  <input
                    #avatarFileInput
                    class="hidden"
                    type="file"
                    accept="image/*"
                    (change)="handleAvatarUpload($event)"
                  />
                  <button
                    type="button"
                    [class]="buttonClasses('outline', 'sm')"
                    [disabled]="uploadingAvatar || isLoading"
                    (click)="avatarFileInput.click()"
                  >
                    <svg
                      *ngIf="uploadingAvatar"
                      lucideIcon="loader-circle"
                      class="h-4 w-4 animate-spin"
                    ></svg>
                    <svg
                      *ngIf="!uploadingAvatar"
                      lucideIcon="upload"
                      class="h-4 w-4"
                    ></svg>
                    {{
                      uploadingAvatar
                        ? language.t("settings.uploadingAvatar")
                        : language.t("settings.uploadAvatar")
                    }}
                  </button>
                </div>
              </div>
            </div>

            <div class="space-y-2">
              <label [class]="labelClasses()" for="display-name">{{
                language.t("settings.explorerName")
              }}</label>
              <input
                [class]="inputClasses()"
                id="display-name"
                name="displayName"
                placeholder="Captain Jack"
                [(ngModel)]="displayName"
              />
            </div>

            <div class="space-y-3 rounded-md border border-border/70 p-3">
              <div class="space-y-1">
                <label [class]="labelClasses()" for="account-email">{{
                  language.t("settings.accountEmail")
                }}</label>
                <p class="text-xs leading-5 text-muted-foreground">
                  {{ language.t("settings.accountEmailDescription") }}
                </p>
              </div>
              <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
                <input
                  [class]="inputClasses('sm:flex-1')"
                  id="account-email"
                  name="accountEmail"
                  type="email"
                  autocomplete="email"
                  [(ngModel)]="email"
                  (keyup.enter)="handleEmailSave()"
                />
                <button
                  type="button"
                  [class]="buttonClasses('outline', 'sm', 'sm:w-auto')"
                  [disabled]="savingEmail || !emailChanged"
                  (click)="handleEmailSave()"
                >
                  <svg
                    *ngIf="savingEmail"
                    lucideIcon="loader-circle"
                    class="h-4 w-4 animate-spin"
                  ></svg>
                  <svg
                    *ngIf="!savingEmail"
                    lucideIcon="mail"
                    class="h-4 w-4"
                  ></svg>
                  {{ language.t("settings.updateEmail") }}
                </button>
              </div>
            </div>

            <div class="space-y-2">
              <label [class]="labelClasses()" for="profile-status-message">{{
                language.t("settings.profileStatusMessage")
              }}</label>
              <textarea
                [class]="inputClasses('min-h-20 resize-y')"
                id="profile-status-message"
                name="profileStatusMessage"
                maxlength="140"
                [placeholder]="
                  language.t('settings.profileStatusMessagePlaceholder')
                "
                [(ngModel)]="profileStatusMessage"
              ></textarea>
            </div>

            <div class="space-y-2">
              <label [class]="labelClasses()" for="profile-hub">{{
                language.t("settings.hub")
              }}</label>
              <select
                [class]="inputClasses()"
                id="profile-hub"
                name="hub"
                [(ngModel)]="hub"
              >
                <option value="">{{ language.t("auth.chooseHub") }}</option>
                <option *ngFor="let option of hubOptions" [value]="option.key">
                  {{ option.label }}
                </option>
              </select>
            </div>

            <button
              (click)="handleSave()"
              [disabled]="isLoading"
              [class]="buttonClasses('default', 'default', 'w-full')"
            >
              <svg
                *ngIf="isLoading"
                lucideIcon="loader-circle"
                class="h-4 w-4 animate-spin mr-2"
              ></svg>
              <svg
                *ngIf="!isLoading"
                lucideIcon="save"
                class="h-4 w-4 mr-2"
              ></svg>
              {{ language.t("settings.saveChanges") }}
            </button>

            <div class="space-y-3 rounded-md border border-border p-4">
              <div>
                <h2 class="text-sm font-semibold text-foreground">
                  {{ language.t("settings.languageTitle") }}
                </h2>
                <p class="text-sm text-muted-foreground">
                  {{ language.t("settings.languageDescription") }}
                </p>
              </div>
              <app-language-selector />
            </div>

            <div class="space-y-3 rounded-md border border-border p-4">
              <div>
                <h2 class="text-sm font-semibold text-foreground">Tutorial</h2>
                <p class="text-sm text-muted-foreground">
                  Review the first steps and game rules whenever you need.
                </p>
              </div>
              <button
                [class]="buttonClasses('outline', 'default', 'w-full')"
                (click)="router.navigateByUrl('/tutorial')"
              >
                <svg lucideIcon="compass" class="h-4 w-4"></svg>
                Review tutorial
              </button>
            </div>

            <div class="pt-6 border-t border-border">
              <button
                [class]="buttonClasses('outline', 'default', 'w-full')"
                (click)="handleSignOut()"
              >
                {{ language.t("common.signOut") }}
              </button>
            </div>

            <div
              class="space-y-3 rounded-md border border-destructive/30 bg-destructive/5 p-4"
            >
              <div>
                <h2 class="text-sm font-semibold text-destructive">
                  {{ language.t("settings.dangerTitle") }}
                </h2>
                <p class="text-sm leading-6 text-muted-foreground">
                  {{ language.t("settings.deleteAccountDescription") }}
                </p>
              </div>
              <button
                type="button"
                [class]="buttonClasses('destructive', 'default', 'w-full')"
                [disabled]="deletingAccount || isLoading"
                (click)="handleDeleteAccount()"
              >
                <svg
                  *ngIf="deletingAccount"
                  lucideIcon="loader-circle"
                  class="h-4 w-4 animate-spin"
                ></svg>
                <svg
                  *ngIf="!deletingAccount"
                  lucideIcon="user-x"
                  class="h-4 w-4"
                ></svg>
                {{ language.t("settings.deleteAccount") }}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  `,
})
export class SettingsPageComponent implements OnInit, OnDestroy {
  readonly auth = inject(AuthService);
  readonly language = inject(LanguageService);
  readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  displayName = "";
  avatarUrl = "";
  email = "";
  originalEmail = "";
  profileStatusMessage = "";
  hub: HubKey | "" = "";
  readonly hubOptions = HUB_OPTIONS;
  isLoading = false;
  savingEmail = false;
  uploadingAvatar = false;
  deletingAccount = false;
  private subscription?: Subscription;

  ngOnInit() {
    this.subscription = new Subscription();
    this.subscription.add(
      this.auth.profile$.subscribe((profile) => this.setProfile(profile)),
    );
    this.subscription.add(
      this.auth.user$.subscribe((user) => this.setUser(user)),
    );
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async handleSave() {
    this.isLoading = true;
    this.changeDetector.detectChanges();
    let { error } = await this.auth.updateProfile({
      display_name: this.displayName.trim() || null,
      avatar_url: this.avatarUrl.trim() || null,
      profile_status_message: this.profileStatusMessage.trim() || null,
      hub: this.hub || null,
    });

    if (error && profileStatusColumnMissing(error)) {
      const fallback = await this.auth.updateProfile({
        display_name: this.displayName.trim() || null,
        avatar_url: this.avatarUrl.trim() || null,
        hub: this.hub || null,
      });
      error = fallback.error;
    }

    this.isLoading = false;
    this.changeDetector.detectChanges();

    if (error) {
      this.toast.error(this.language.t("settings.updateError"));
    } else {
      this.toast.success(this.language.t("settings.updateSuccess"));
    }
  }

  get emailChanged() {
    return this.email.trim().toLowerCase() !== this.originalEmail;
  }

  async handleEmailSave() {
    const nextEmail = this.email.trim().toLowerCase();

    if (!isValidEmail(nextEmail)) {
      this.toast.error(this.language.t("settings.emailInvalid"));
      return;
    }

    if (nextEmail === this.originalEmail) {
      this.toast.info(this.language.t("settings.emailUnchanged"));
      return;
    }

    this.savingEmail = true;
    this.changeDetector.detectChanges();
    const { error } = await this.auth.updateEmail(nextEmail);
    this.savingEmail = false;
    this.changeDetector.detectChanges();

    if (error) {
      this.toast.error(
        isEmailAlreadyRegisteredError(error)
          ? this.language.t("settings.emailAlreadyRegistered")
          : this.language.t("settings.emailUpdateError"),
      );
      return;
    }

    this.email = nextEmail;
    this.originalEmail = nextEmail;
    this.toast.success(this.language.t("settings.emailUpdateSuccess"));
  }

  async handleAvatarUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = "";

    if (!file) return;
    if (!file.type.startsWith("image/")) {
      this.toast.error(this.language.t("settings.avatarImageOnly"));
      return;
    }

    const user = this.auth.user$.value;
    if (!user) {
      await this.router.navigateByUrl("/auth");
      return;
    }

    this.uploadingAvatar = true;
    this.changeDetector.detectChanges();

    try {
      const extension = file.name.split(".").pop()?.toLowerCase() || "png";
      const path = `${user.id}/${crypto.randomUUID()}.${extension}`;
      const { data: uploadData, error: uploadError } = await backend.storage
        .from("avatars")
        .upload(path, file, { contentType: file.type, upsert: true });
      if (uploadError) throw uploadError;

      const { data: publicData } = backend.storage
        .from("avatars")
        .getPublicUrl(path);
      const imageUrl = uploadData?.publicUrl || publicData.publicUrl;
      const { error: updateError } = await this.auth.updateProfile({
        avatar_url: imageUrl,
      });
      if (updateError) throw updateError;

      this.avatarUrl = imageUrl;
      this.toast.success(this.language.t("settings.avatarUploadSuccess"));
    } catch (error) {
      console.error("Error uploading avatar:", error);
      const details = errorMessage(error);
      this.toast.error(
        details
          ? `${this.language.t("settings.avatarUploadError")} ${details}`
          : this.language.t("settings.avatarUploadError"),
      );
    } finally {
      this.uploadingAvatar = false;
      this.changeDetector.detectChanges();
    }
  }

  async handleSignOut() {
    await this.auth.signOut();
    await this.router.navigateByUrl("/auth");
  }

  async handleDeleteAccount() {
    const confirmation = window.prompt(
      this.language.t("settings.deleteAccountConfirm", { phrase: "DELETE" }),
    );
    if (confirmation === null) return;

    if (confirmation.trim() !== "DELETE") {
      this.toast.error(this.language.t("settings.deleteAccountConfirmError"));
      return;
    }

    this.deletingAccount = true;
    this.changeDetector.detectChanges();
    try {
      const { error } = await backend.rpc("delete_own_account");
      if (error) throw error;

      await this.auth.signOut();
      this.toast.success(this.language.t("settings.deleteAccountSuccess"));
      await this.router.navigateByUrl("/auth");
    } catch (error) {
      console.error("Error deleting account:", error);
      this.toast.error(this.language.t("settings.deleteAccountError"));
    } finally {
      this.deletingAccount = false;
      this.changeDetector.detectChanges();
    }
  }

  private setProfile(profile: Profile | null) {
    this.displayName = profile?.display_name || "";
    this.avatarUrl = profile?.avatar_url || "";
    this.profileStatusMessage = profile?.profile_status_message || "";
    this.hub = normalizeHub(profile?.hub) || "";
    this.changeDetector.detectChanges();
  }

  private setUser(user: User | null) {
    this.email = user?.email || "";
    this.originalEmail = this.email.trim().toLowerCase();
    this.changeDetector.detectChanges();
  }

  buttonClasses = buttonClasses;
  cardClasses = cardClasses;
  inputClasses = inputClasses;
  labelClasses = labelClasses;
}
